import { Link, useLocation } from 'react-router-dom';
import { Search, Menu, X, Filter } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { setSearchQuery } from '../../store/slices/searchSlice';
import { toggleFilterDrawer } from '../../store/slices/archiveSlice';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useState, useRef, useEffect } from 'react';

interface MobileHomeHeaderProps {
  onMobileSidebarToggle?: () => void;
}

const MobileHomeHeader: React.FC<MobileHomeHeaderProps> = ({ 
  onMobileSidebarToggle 
}) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const searchQuery = useSelector((state: RootState) => state.search.query);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const { openSignupModal } = useAuthModal();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Show filter button only on archives page
  const showFilterButton = location.pathname === '/archives';

  // Focus input when search expands
  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  const handleSearchToggle = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (isSearchExpanded) {
      // Clear search when closing
      dispatch(setSearchQuery(''));
    }
  };

  return (
    <div className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-gray-200/50 shadow-sm">
      <div className="flex items-center h-14 px-4 relative">
        {/* Logo - Hidden when search is expanded */}
        {!isSearchExpanded && (
          <Link to="/" className="transition-transform active:scale-95 duration-200 flex items-center">
            <img 
              src="/Creatorsarchive1.2@4x.png" 
              alt="Creators Archive" 
              className="h-6 w-auto"
            />
          </Link>
        )}

        {/* Expanded Search Input - Overlays logo */}
        {isSearchExpanded && (
          <div className="absolute left-4 right-20 flex items-center animate-in fade-in slide-in-from-left-4 duration-200">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 stroke-[1.5]" strokeLinecap="round" strokeLinejoin="round" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => dispatch(setSearchQuery(e.target.value))}
                className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full border-0 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:bg-white transition-all placeholder:text-gray-400"
              />
            </div>
          </div>
        )}

        <div className="flex-1" /> {/* Spacer */}

        {/* Join as a Designer - prospective designers, hidden while searching */}
        {!isAuthenticated && !isSearchExpanded && (
          <button
            onClick={() => openSignupModal({ accountType: 'creator' })}
            className="mr-2 px-3 h-8 rounded-full bg-gray-900 text-white text-xs font-semibold whitespace-nowrap active:scale-95 transition-transform duration-200"
          >
            Join as a Designer
          </button>
        )}

        {/* Filter Icon Button - Only on archives page */}
        {showFilterButton && (
          <button
            onClick={() => dispatch(toggleFilterDrawer())}
            className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-200 mr-1"
            aria-label="Open filters"
          >
            <Filter className="h-5 w-5 text-gray-900" strokeWidth={2.5} />
          </button>
        )}

        {/* Search Icon Button */}
        <button
          onClick={handleSearchToggle}
          className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-200 mr-1"
          aria-label={isSearchExpanded ? "Close search" : "Open search"}
        >
          {isSearchExpanded ? (
            <X className="h-5 w-5 text-gray-900" strokeWidth={2.5} />
          ) : (
            <Search className="h-5 w-5 text-gray-900" strokeWidth={2.5} />
          )}
        </button>

        {/* Hamburger Menu - Show when user is logged in */}
        {isAuthenticated && (
          <button
            onClick={onMobileSidebarToggle}
            className="flex items-center justify-center w-9 h-9 -mr-2 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-200"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-gray-900" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileHomeHeader;
